<template>
  <BaseDrawer
    no-padding
    height-auto-only
    no-toolbar
    no-bottom-rounding
    class="scrollbar-hide"
    close-label="Lens creation"
    @close="$emit('close')"
  >
    <template #content>
      <div class="w-full flex flex-col" style="height: calc(100vh - 80px)">
        <!-- TOOLBAR -->
        <div class="w-full flex items-center h-14 justify-between px-4 py-3 border-b border-border-normal flex-shrink-0">
          <!-- Label -->
          <div class="flex items-center p-1.5 gap-1">
            <LensIcon class="text-icon-muted" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Create a New Lens Brand
            </BaseText>
          </div>
          <!-- Exit -->
          <button class="p-1.5" @click="$emit('close')">
            <TagRemoveIcon stroke="#5E6678" />
          </button>
        </div>
        <!-- CONTENT -->
        <div class="flex-grow w-full flex justify-center p-11 min-h-0 overflow-y-scroll">
          <div class="w-full flex flex-col" style="max-width: 600px">
            <!-- BRAND DETAILS -->
            <CreationStep
              label="Give us some details about your brand"
              :isComplete="formSectionStatus.brand.isComplete"
            >
              <div class="w-full flex flex-col gap-4">
                <!-- Name Input -->
                <BaseInputV3
                  label="Brand Name"
                  placeholder="Enter your brand name"
                  :inputValue="form.brand.name"
                  @update:inputValue="updateFormField('brand', 'name', $event)"
                />
                <!-- Industry Dropdown -->
                <BaseSingleDropdown
                  show-label-space
                  label="Industry / Niche"
                  :options="industryOptions"
                  class="z-30"
                  option-key="label"
                  search-key="label"
                  enable-search
                  placeholder="Select an industry"
                  search-placeholder="Search Industries"
                  :selected-obj="form.brand.industry"
                  use-parent-width
                  :max-height="328"
                  @change="updateFormField('brand', 'industry', $event)"
                />
                <!-- AOV Select -->
                <BigButtonGroup 
                  label="Average Order Value (AOV)"
                  :options="aovOptions"
                  :selected-value="form.brand.aov"
                  render-key="aov"
                  @change="updateFormField('brand', 'aov', $event)"
                />
                <!-- Revenue Select -->
                <BigButtonGroup 
                  label="GMV/Revenue"
                  :options="revenueOptions"
                  :selected-value="form.brand.revenue"
                  render-key="revenue"
                  @change="updateFormField('brand', 'revenue', $event)"
                />
              </div>
            </CreationStep>
            <!-- DATA SOURCES -->
            <CreationStep
              label="Choose / Connect your data sources"
              :isComplete="formSectionStatus.dataSources.isComplete"
            >
              <div class="w-full flex flex-col">
                <DataSourceSelector 
                  platform="meta"
                  :ad-account-options="adAccountOptions.meta"
                  :selected-accounts="form.dataSources.meta.map(a => a.id)"
                  :loading="loadingAdAccounts"
                  :loadingAuthURL="loadingAuthURL"
                  @select="handleDataSourceSelect('meta', $event)"
                  @authenticate="handlePlatformAuthenticate('meta')"
                />
                <DataSourceSelector 
                  class="mt-3"
                  platform="tiktok"
                  :ad-account-options="adAccountOptions.tiktok"
                  :selected-accounts="form.dataSources.tiktok.map(a => a.id)"
                  :loading="loadingAdAccounts"
                  :loadingAuthURL="loadingAuthURL"
                  @select="handleDataSourceSelect('tiktok', $event)"
                  @authenticate="handlePlatformAuthenticate('tiktok')"
                />
                <transition name="currency">
                  <div v-if="currencyOptions.length" class="w-full flex flex-col justify-end gap-1" style="height: 68px;">
                    <div class="w-full flex items-center gap-1">
                      <BaseText type="label" size="sm" class="text-text-muted">
                        Currency
                      </BaseText>
                      <!-- Info Tooltip -->
                      <div class="relative">
                        <transition>
                          <ShowMoreInfoIcon v-if="currencyOptions.length > 1" class="text-text-disabled transition-colors hover:text-text-subdued"
                          @mouseenter.native="showCurrencyInfoTooltip = true" @mouseleave.native="showCurrencyInfoTooltip = false" />
                        </transition>
                        <transition name="slow-fade">
                          <div v-if="showCurrencyInfoTooltip" class="info-tooltip flex flex-col gap-1.5 bg-neutral-700 rounded-md p-2.5 shadow-md">
                            <BaseText type="body" size="xs" class="text-white">
                              For multiple data sources with different currencies, you must select one to use for currency display.
                            </BaseText>
                            <BaseText type="body" size="xs" class="text-neutral-alpha-650">
                              All other currencies will be converted to the one selected here.
                            </BaseText>
                          </div>
                        </transition>
                      </div>
                    </div>
                    <BaseSingleDropdown 
                      class="z-30"
                      use-parent-width
                      :disabled="currencyOptions.length < 2"
                      :options="currencyOptions"
                      option-key="value"
                      :selected-obj="selectedCurrency"
                      @change="handleCurrencySelect($event)"
                    />
                  </div>
                </transition>
              </div>
            </CreationStep>
            <!-- GOAL METRICS -->
            <CreationStep
              label="Select your goal metric"
              :isComplete="formSectionStatus.goalMetric.isComplete"
              hide-border
            >
              <div class="goal-metric-container relative w-full px-px" :style="{ height: `${goalMetricContainerHeight}px` }"
              :class="{ 'overflow-y-hidden': goalMetricsTransitioning }" @transitionstart="handleGoalMetricTransiton($event, true)" @transitionend="handleGoalMetricTransiton($event, false)">
                <transition-group name="goal-metric" mode="out-in" tag="div" class="w-full flex flex-col gap-5">
                  <GoalMetricSelector 
                    v-if="activeGoalMetrics.meta"
                    key="meta-goal-metric"
                    platform="meta"
                    :selected-metric="form.goalMetric.meta"
                    :metrics-override="platformMetrics.meta"
                    :loading-metrics="loadingPlatformMetrics"
                    @changeMetric="updateFormField('goalMetric', 'meta', $event)"
                  />
                  <GoalMetricSelector 
                    v-if="activeGoalMetrics.tiktok"
                    key="tiktok-goal-metric"
                    platform="tiktok"
                    :selected-metric="form.goalMetric.tiktok"
                    :metrics-override="platformMetrics.tiktok"
                    :loading-metrics="loadingPlatformMetrics"
                    @changeMetric="updateFormField('goalMetric', 'tiktok', $event)"
                  />
                </transition-group>
              </div>
            </CreationStep>
            <!-- Submit -->
            <div class="relative bottom-4 pb-40 pl-14 pr-5">
              <button class="submit-btn w-full px-2.5 py-1.5 rounded-md flex items-center justify-center"
              :class="submitEnabled ? 'enabled' : 'disabled cursor-default'" :disabled="loadingSubmit"
              @click="handleSubmit">
                <BaseLoadingSpinnerCircle v-if="loadingSubmit" :duration="1.2" class="text-icon-disabled" />
                <BaseText v-else type="label" size="sm" class="transition-colors duration-100" :class="submitEnabled ? 'text-white' : 'text-text-disabled'">
                  Create Lens Brand
                </BaseText>
              </button>
            </div>
          </div>
        </div>
        <!-- Authentication Controller Interface -->
        <LensPlatformAuthenticator 
          ref="platformAuthenticator"
          :loading="loadingAuthURL"
        />
      </div>
    </template>
  </BaseDrawer>
</template>

<script>
import { mapActions } from 'vuex'
import LensAPI from '@/api/lens'
import industryOptions from '@/utils/lens/industryNiches'
import { formatAdAccountsByPlatform } from '@/utils/lens/dataSourceUtils'

// Components
import CreationStep from './CreationStep.vue'
import BigButtonGroup from './BigButtonGroup.vue'
import DataSourceSelector from './DataSourceSelector.vue'
import GoalMetricSelector from './GoalMetricSelector.vue'

// Controllers
import LensPlatformAuthenticator from '../../LensPlatformAuthenticator.vue'

// Icons
import LensIcon from '../../../globals/Icons/SidebarIcons/LensIcon.vue'
import TagRemoveIcon from '../../../globals/Icons/TagRemoveIcon.vue'
import ShowMoreInfoIcon from '../../../globals/Icons/ShowMoreInfoIcon.vue'

export default {
  name: 'LensCreationDrawer',
  components: {
    CreationStep,
    BigButtonGroup,
    DataSourceSelector,
    GoalMetricSelector,
    LensPlatformAuthenticator,
    LensIcon,
    TagRemoveIcon,
    ShowMoreInfoIcon
  },
  data () {
    return {
      // Options
      industryOptions,
      aovOptions: [
        { label: '<$100', value: '<$100', action: () => { this.updateFormField('brand', 'aov', '<$100') } },
        { label: '$100+', value: '$100+', action: () => { this.updateFormField('brand', 'aov', '$100+') } }
      ],
      revenueOptions: [
        { label: '<$1M', value: '<$1M', action: () => { this.updateFormField('brand', 'revenue', '<$1M') } },
        { label: '$1M - $10M', value: '$1M-$10M', action: () => { this.updateFormField('brand', 'revenue', '$1M-$10M') } },
        { label: '$10M+', value: '$10M+', action: () => { this.updateFormField('brand', 'revenue', '$10M+') } }
      ],
      adAccountOptions: {
        meta: [],
        tiktok: []
      },
      platformMetrics: {},
      
      // Form States
      form: {
        brand: {
          name: '',
          industry: null,
          aov: null,
          revenue: null
        },
        dataSources: {
          meta: [],
          tiktok: []
        },
        currency: null,
        goalMetric: {
          meta: null,
          tiktok: null
        }
      },
      
      // UI
      loadingAdAccounts: true,
      loadingPlatformMetrics: true,
      loadingSubmit: false,
      goalMetricsTransitioning: false,
      loadingAuthURL: false,
      authPopupInterval: null,
      showCurrencyInfoTooltip: false
    }
  },
  async mounted () {
    await Promise.all([
      this.fetchAdAccounts(),
      this.fetchPlatformMetrics()
    ])
  },
  beforeDestroy () {
    if (this.authPopupInterval) {
      clearInterval(this.authPopupInterval)
    }
  },
  watch: {
    'form.dataSources': {
      deep: true,
      handler (newSources) {
        Object.entries(newSources).forEach(([platform, accounts]) => {
          if (!accounts.length) {
            this.form.goalMetric[platform] = null
          } else if (!this.form.currency) {
            this.form.currency = accounts[0]?.currency ?? null
          }
        })
      }
    },
    selectedCurrency (newCurrency) {
      if (!newCurrency) {
        // If the user removed an ad account using the currency they selected, perform a fallback
        this.form.currency = this.currencyOptions[0]?.value ?? null
      }
    }
  },
  computed: {
    currencyOptions () {
      const selectedAdAccounts = Object.values(this.form.dataSources).flat()
      const currencies = [...new Set(selectedAdAccounts.map(account => account.currency))]
      return currencies.map(currency => ({
        label: `${getCurrencyName(currency)} (${currency})`,
        value: currency
      }))
    },
    selectedCurrency () {
      return this.currencyOptions.find(currency => currency.value === this.form.currency) ?? null
    },
    formSectionStatus () {
      const { brand, dataSources, goalMetric } = this.form
      const activeSelectors = Object.values(this.activeGoalMetrics).filter(Boolean).length
      return {
        brand: {
          isComplete: !!(
            brand.name.length > 0 &&
            brand.industry &&
            brand.aov &&
            brand.revenue
          ),
          incompleteMessage: 'You must complete the brand details section'
        },
        dataSources: {
          isComplete: dataSources.meta.length > 0 || dataSources.tiktok.length > 0,
          incompleteMessage: 'You must connect at least one data source'
        },
        goalMetric: {
          isComplete: !!(
            (dataSources.meta.length > 0 || dataSources.tiktok.length > 0) &&
            (!dataSources.meta.length || goalMetric.meta) &&
            (!dataSources.tiktok.length || goalMetric.tiktok)
          ),
          incompleteMessage: `You must select a goal metric${activeSelectors > 1 ? ' for each connected platform' : ''}`
        }
      }
    },
    formIsValid () {
      return Object.values(this.formSectionStatus).every(section => section.isComplete)
    },
    submitEnabled () {
      return this.formIsValid && !this.loadingSubmit
    },
    activeGoalMetrics () {
      return {
        meta: this.form.dataSources.meta.length || !this.form.dataSources.tiktok.length,
        tiktok: this.form.dataSources.tiktok.length
      }
    },
    goalMetricContainerHeight () {
      const activeSelectors = Object.values(this.activeGoalMetrics).filter(Boolean).length
      return 112 + ((activeSelectors - 1) * 132) // px
    }
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    async fetchAdAccounts () {
      // Fetch the ad accounts
      this.loadingAdAccounts = true
      try {
        const { data: allProviders } = await LensAPI.Lens.getPlatformAccounts()
        this.adAccountOptions = formatAdAccountsByPlatform(allProviders)
        this.loadingAdAccounts = false
      } catch (error) {
        console.error('Failed to get ad accounts:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch ad accounts. See console for details'
        })
        this.$emit('close')
      } finally {
        this.loadingAdAccounts = false
      }
    },
    async fetchPlatformMetrics () {
      this.loadingPlatformMetrics = true
      try {
        const platformMetrics = await LensAPI.LensMetrics.getLensMetrics()
        this.platformMetrics = platformMetrics
      } catch (error) {
        console.error('Failed to fetch platform metrics:', error)
      } finally {
        this.loadingPlatformMetrics = false
      }
    },
    async handleSubmit () {
      // Check form validity
      if (!this.formIsValid) {
        Object.values(this.formSectionStatus).forEach(section => {
          if (!section.isComplete) {
            this.$showAlert({
              type: 'info',
              message: section.incompleteMessage,
              timeout: 2500
            })
          }
        })
        return
      }

      // Submit form
      this.loadingSubmit = true
      try {
        // Build the creation payload
        const { brand, dataSources, currency, goalMetric } = this.form

        // Parse AOV values correctly
        const aovMap = {
          '<$100': { aov_min: 0, aov_max: 100 },
          '$100+': { aov_min: 100, aov_max: null },
        }
        const { aov_min = null, aov_max = null } = aovMap[brand.aov] || {}

        // Parse GMV/Revenue values correctly
        const gmvMap = {
          '<$1M': 999999,
          '$1M-$10M': 5000000,
          '$10M+': 10000000,
        }
        const gmv = gmvMap[brand.revenue] ?? null

        // Build the ad accounts payload
        const adAccounts = Object.values(dataSources).flat().map(account => ({
          account_id: account.id,
          account_name: account.name,
          business_id: account.businessId,
          provider: account.serverProviderKey
        }))

        const payload = {
          name: brand.name,
          ad_accounts: adAccounts,
          company_name: brand.name,
          currency: currency,
          timezone: '',
          goal_metric: goalMetric.meta || 'roas',
          tiktok_goal_metric: goalMetric.tiktok || 'roas',
          spend_threshold: 0,
          click_attribution_window: '7d',
          view_attribution_window: '1d',
          niche: brand.industry.value,
          aov_min: aov_min,
          aov_max: aov_max,
          gmv: gmv,
          description: ''
        }

        await LensAPI.Lens.create(payload)
        await this.fetchSidebarLenses()
        this.$emit('created')
        this.$emit('close')
        this.$showAlert({
          type: 'success',
          message: 'Lens Brand created successfully'
        })
      } catch (error) {
        console.error('Failed to submit form:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to create Lens Brand. See console for details'
        })
      } finally {
        this.loadingSubmit = false
      }
    },
    async handlePlatformAuthenticate (platform) {
      this.loadingAuthURL = true
      try {
        await this.$refs.platformAuthenticator.handlePlatformAuthenticate(platform)
        await this.fetchAdAccounts()
      } catch (error) {
        console.error('Failed to authenticate platform:', error)
      } finally {
        this.loadingAuthURL = false
      }
    },
    updateFormField (section, field, value) {
      this.form[section][field] = value
    },
    handleDataSourceSelect (platform, account) {
      const existingAccounts = this.form.dataSources[platform]
      if (existingAccounts.some(a => a.id === account.id)) {
        this.form.dataSources[platform] = existingAccounts.filter(a => a.id !== account.id)
      } else {
        this.form.dataSources[platform] = [...existingAccounts, account]
      }
    },
    handleCurrencySelect (currencyObj) {
      this.form.currency = currencyObj.value
    },
    // UI
    handleGoalMetricTransiton (event, isStart) {
      if (event.target !== event.currentTarget) return
      this.goalMetricsTransitioning = isStart
    }
  }
}

const getCurrencyName = (code, locale = "en") => {
  const dn = new Intl.DisplayNames([locale], { type: "currency" });
  return dn.of(String(code).toUpperCase()) ?? code;
}
</script>

<style scoped>
.goal-metric-container {
  transition: height 250ms ease-in-out;
}
.info-tooltip {
  position: absolute;
  width: 280px;
  bottom: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 500;
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.slow-fade-enter-active, .slow-fade-leave-active {
  transition: opacity 200ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to, .slow-fade-enter-from, .slow-fade-enter, .slow-fade-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from, .slow-fade-enter-to, .slow-fade-leave-from {
  opacity: 1;
}
.goal-metric-enter-active {
  transition: opacity 250ms ease-in-out;
}
.goal-metric-enter-from, .goal-metric-enter {
  opacity: 0;
}
.goal-metric-enter-to {
  opacity: 1;
}
.currency-enter-active {
  animation: currencyEnter 350ms ease-in-out forwards;
}
.currency-leave-active {
  animation: currencyLeave 350ms ease-in-out forwards;
}
@keyframes currencyEnter {
  0% { opacity: 0; height: 0px; }
  50% { opacity: 0; height: 68px; }
  100% { opacity: 1; height: 68px; }
}
@keyframes currencyLeave {
  0% { opacity: 1; height: 68px; }
  50% { opacity: 0; height: 68px; }
  100% { opacity: 0; height: 0px; }
}
</style>